// Helpers de formatage (Intl fonctionne parfaitement en web, contrairement à Hermes).

export function formatPrice(
  cents: number,
  currency: string,
  locale: string,
): string {
  return new Intl.NumberFormat(locale === 'fr' ? 'fr-FR' : 'en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(cents / 100);
}

// Temps relatif court (ex. « il y a 2 h », « 3 j »). Pour les commentaires.
export function formatRelativeTime(iso: string, locale: string): string {
  const loc = locale === 'fr' ? 'fr-FR' : 'en-US';
  const diffMs = new Date(iso).getTime() - Date.now();
  const rtf = new Intl.RelativeTimeFormat(loc, {
    numeric: 'auto',
    style: 'short',
  });
  const abs = Math.abs(diffMs);
  const min = 60_000;
  const h = 60 * min;
  const d = 24 * h;
  if (abs < h) return rtf.format(Math.round(diffMs / min), 'minute');
  if (abs < d) return rtf.format(Math.round(diffMs / h), 'hour');
  if (abs < 30 * d) return rtf.format(Math.round(diffMs / d), 'day');
  return new Intl.DateTimeFormat(loc, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(new Date(iso));
}

// Date / période d'un souvenir (YYYY-MM-DD) — PORT À L'IDENTIQUE de
// mobile/src/components/memory/cardFormat.ts (formatDateRange).
//
// Plage COMPACTE : quand début et fin partagent le mois et/ou l'année, la partie commune
// n'est pas répétée. L'assemblage est fait à la main parce que la STRUCTURE diffère selon la
// langue (jour d'abord en français, mois d'abord en anglais) ; seuls les noms de mois sont
// localisés par Intl.
//   fr : « 10–24 oct. 2022 » (même mois) · « 10 févr. – 26 mars 2026 » (même année)
//   en : « Oct 10–24, 2022 »             · « Feb 10 – Mar 26, 2026 »
// Années différentes → aucune fusion, les deux dates sont écrites en entier.
// L'année est TOUJOURS conservée, y compris sous un séparateur d'année : la parité avec le
// mobile prime, et la retirer produirait des cas ambigus à cheval sur deux années.
const MEMORY_DATE_OPTS = {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
} as const;

// `YYYY-MM-DD` est une date PURE : on force minuit LOCAL, sinon un parsing UTC décalerait
// l'affichage d'un jour selon le fuseau.
function toMemoryDate(iso: string): Date | null {
  const date = new Date(`${iso}T00:00:00`);
  return Number.isNaN(date.getTime()) ? null : date;
}

function ymd(iso: string): { y: number; m: number; d: number } {
  const [y, m, d] = iso.split('-').map(Number);
  return { y, m, d };
}

function formatOneMemoryDate(iso: string, locale: string): string {
  const date = toMemoryDate(iso);
  return date ? date.toLocaleDateString(locale, MEMORY_DATE_OPTS) : iso;
}

function monthShort(date: Date, locale: string): string {
  return date.toLocaleDateString(locale, { month: 'short' });
}

export function formatMemoryDate(
  start: string | null,
  end: string | null,
  locale: string,
): string {
  if (!start) return '';
  const startLabel = formatOneMemoryDate(start, locale);
  if (!end || end === start) return startLabel;

  const s = toMemoryDate(start);
  const e = toMemoryDate(end);
  const ps = ymd(start);
  const pe = ymd(end);
  // Dates invalides OU années différentes → format complet, aucune fusion.
  if (!s || !e || ps.y !== pe.y)
    return `${startLabel} – ${formatOneMemoryDate(end, locale)}`;

  const isFr = locale.toLowerCase().startsWith('fr');
  const sMonth = monthShort(s, locale);
  const eMonth = monthShort(e, locale);
  const sameMonth = ps.m === pe.m;

  if (isFr) {
    return sameMonth
      ? `${ps.d}–${pe.d} ${eMonth} ${pe.y}`
      : `${ps.d} ${sMonth} – ${pe.d} ${eMonth} ${pe.y}`;
  }
  return sameMonth
    ? `${eMonth} ${ps.d}–${pe.d}, ${pe.y}`
    : `${sMonth} ${ps.d} – ${eMonth} ${pe.d}, ${pe.y}`;
}

// Octets → « X Go » / « X GB » (base 1024).
export function formatStorage(bytes: number, locale: string): string {
  const gib = bytes / 1024 ** 3;
  const value = new Intl.NumberFormat(locale === 'fr' ? 'fr-FR' : 'en-US', {
    maximumFractionDigits: gib < 10 && gib % 1 !== 0 ? 1 : 0,
  }).format(gib);
  return `${value} ${locale === 'fr' ? 'Go' : 'GB'}`;
}

/**
 * Format compact d'un compteur (likes, commentaires, pastilles) — PORT À L'IDENTIQUE de
 * `mobile/src/utils/formatCount.ts`. Deux plateformes qui abrègent différemment le même
 * nombre, c'est un bug visible : ne pas diverger.
 *
 * Le maximum affiché en clair est 999 ; au-delà, notation compacte : 1k, 1,4k, 20,3k, 8m…
 * PLAFOND DE TROIS CHIFFRES : la décimale saute dès qu'elle ferait un quatrième chiffre
 * (« 999,9k » devient « 999k »), parce qu'un compteur vit dans un espace étroit.
 * Séparateur décimal localisé (virgule en français). Troncature (floor) à une décimale →
 * on ne SUR-compte jamais (1999 → « 1,9k », pas « 2k »).
 */
export function formatCount(n: number, locale: string): string {
  // 0 → 999 : nombre brut (3 caractères max).
  if (n < 1000) return String(n);
  const sep = locale?.startsWith('fr') ? ',' : '.';
  // Milliers (< 1 M) → « k » ; millions → « m ».
  if (n < 1_000_000) return compactCount(n / 1000, sep) + 'k';
  return compactCount(n / 1_000_000, sep) + 'm';
}

// Une décimale par TRONCATURE (jamais d'arrondi au-dessus), sans « ,0 » superflu :
// 1.0 → « 1 », 1.45 → « 1,4 », 20.34 → « 20,3 », 999.99 → « 999 » (plafond).
function compactCount(x: number, sep: string): string {
  // Trois chiffres entiers : plus de place pour une décimale sans passer à quatre.
  if (x >= 100) return String(Math.floor(x));
  const truncated = Math.floor(x * 10) / 10;
  const s = Number.isInteger(truncated)
    ? String(truncated)
    : truncated.toFixed(1);
  return s.replace('.', sep);
}
